import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Twilio A2P 10DLC compliant SMS opt-in checkbox.
 *
 * IMPORTANT for Twilio compliance:
 *  - The checkbox is OPTIONAL — form submission must work whether or not
 *    it is checked.
 *  - Defaults to unchecked (unbundled from Terms of Service).
 *  - Provides the standard STOP/HELP + rate disclosure text.
 *
 * Usage:
 *   const [smsOptIn, setSmsOptIn] = useState(false);
 *   ...
 *   <SmsOptInCheckbox checked={smsOptIn} onChange={setSmsOptIn} />
 */
export const SmsOptInCheckbox = ({ checked, onChange, className = '' }) => (
  <label
    className={`flex items-start gap-2 mt-2 cursor-pointer select-none ${className}`}
    data-testid="sms-opt-in-label"
  >
    <input
      type="checkbox"
      checked={!!checked}
      onChange={(e) => onChange(e.target.checked)}
      className="mt-0.5 h-4 w-4 flex-shrink-0 rounded border-gray-300 text-[#BCA480] focus:ring-[#BCA480] cursor-pointer"
      data-testid="sms-opt-in-checkbox"
    />
    <span
      className="text-[11px] leading-snug text-gray-600"
      data-testid="sms-opt-in-text"
    >
      <strong>(Optional)</strong> I agree to receive recurring text messages from the{' '}
      <strong>Bespoke Realty SMS Program</strong> regarding my inquiry, tours, and
      transaction updates. Recurring messages, up to 10 messages per month. Message
      and data rates may apply. Reply STOP to unsubscribe or HELP for help
      (or call 901-390-4414). See our{' '}
      <Link to="/terms" className="underline hover:text-[#BCA480]" onClick={(e) => e.stopPropagation()} data-testid="sms-opt-in-terms-link">
        Terms &amp; Conditions
      </Link>{' '}
      and{' '}
      <Link to="/privacy" className="underline hover:text-[#BCA480]" onClick={(e) => e.stopPropagation()} data-testid="sms-opt-in-privacy-link">
        Privacy Policy
      </Link>
      . No mobile information will be shared with third parties or affiliates for
      marketing or promotional purposes. Consent to SMS is not required to use our services.
    </span>
  </label>
);

/**
 * Legacy static disclosure — kept for backwards compatibility. Prefer
 * SmsOptInCheckbox above for anything Twilio-facing.
 */
export const SmsConsentDisclosure = ({ className = '' }) => (
  <p
    className={`text-[11px] leading-snug text-gray-500 mt-1.5 ${className}`}
    data-testid="sms-consent-disclosure"
  >
    By providing your phone number, you agree to receive text messages from
    Bespoke Realty regarding your inquiry. Message and data rates may apply.
    Message frequency varies. Reply STOP to unsubscribe. Reply HELP for help.
    See our{' '}
    <Link to="/privacy" className="underline hover:text-[#BCA480]">
      Privacy Policy
    </Link>
    .
  </p>
);

export default SmsOptInCheckbox;
